import React from 'react';
import { twMerge } from 'tailwind-merge';
import { SparkChart } from '@plerionhq/design-system/experimental';
import { Card, CardContent, CardFooter } from '../Card';
import { renderIcon } from '../Dashboard/iconUtils';
import { cn } from '../../../lib/utils';

import { useTranslation } from 'react-i18next';

export interface MetricCardProps {
  /** The title/label for the metric */
  title?: string;
  /** The main metric value to display */
  value?: string | number;
  /** Optional change indicator (e.g., "+20.1% from last month") */
  change?: string;
  /** Whether the change is positive (for styling) */
  isPositiveChange?: boolean;
  /** Optional chart data points for the mini chart */
  chartData?: number[];
  /** Optional chart color */
  chartColor?: string;
  /** Optional custom className for styling */
  className?: string;
  /** Optional icon to display (Lucide icon name as string) */
  icon?: string;
  /** Optional click handler */
  onClick?: () => void;
  /** Whether the card is loading */
  isLoading?: boolean;
  /** Optional subtitle */
  subtitle?: string;
  /** Whether data is streaming from LLM */
  isStreaming?: boolean;
}

export const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  change,
  isPositiveChange = true,
  chartData,
  chartColor = '#f97316',
  className,
  icon,
  onClick,
  isLoading = false,
  subtitle,
}) => {
  const { t } = useTranslation('pleri/dashboard');
  // Show loading state if explicitly loading or if no data is provided
  if (isLoading || (!title && !value && !subtitle && !change)) {
    return (
      <Card className={cn('h-full', className)} onClick={onClick}>
        <CardContent className="flex h-full items-center p-6">
          <div className="w-full animate-pulse">
            <div className="mb-2 h-4 w-24 rounded bg-gray-200 dark:bg-gray-700"></div>
            <div className="mb-2 h-8 w-32 rounded bg-gray-200 dark:bg-gray-700"></div>
            <div className="h-3 w-20 rounded bg-gray-200 dark:bg-gray-700"></div>
          </div>
        </CardContent>
      </Card>
    );
  }
  const formatValue = (val: string | number) => {
    if (typeof val === 'number') {
      return val.toLocaleString();
    }
    return val;
  };

  const renderChart = () => {
    if (!chartData || chartData.length === 0) {
      return null;
    }

    // Convert chartData to design system format
    const sparkChartData = chartData.map((value, index) => ({
      category: index.toString(),
      value,
    }));

    return (
      <div className="mt-4 h-16 w-full">
        <SparkChart
          data={sparkChartData as Record<string, string | number>[]}
          series={['value']}
          type="area"
          categoryKey="category"
          className="h-full w-full"
          showTooltip={false}
          yAxisProps={{
            domain: ['+min', '+max'],
          }}
          colors={[chartColor || '#f97316']} // Orange color to match the original
          seriesProps={{ isAnimationActive: false }}
        />
      </div>
    );
  };

  const renderLoadingContent = () => (
    <div className="animate-pulse">
      <div className="mb-2 h-4 w-24 rounded bg-gray-200 dark:bg-gray-700"></div>
      <div className="mb-2 h-8 w-32 rounded bg-gray-200 dark:bg-gray-700"></div>
      <div className="h-3 w-20 rounded bg-gray-200 dark:bg-gray-700"></div>
    </div>
  );

  return (
    <Card
      className={cn('h-full', className)}
      onClick={onClick}
      isLoading={isLoading}
      loadingContent={renderLoadingContent()}
    >
      <div className="flex h-full flex-col">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="mb-2 flex items-center gap-2">
              {icon && (
                <div className="text-gray-600 dark:text-gray-400">
                  {renderIcon({ name: icon })}
                </div>
              )}
              <div className="text-sm font-normal text-gray-600 dark:text-gray-400">
                {title || t('metric')}
              </div>
            </div>
          </div>
        </div>

        <CardContent className="flex flex-1 flex-col justify-center">
          <div className="mb-2">
            <div className="text-2xl leading-tight font-semibold tracking-tight text-gray-900 dark:text-gray-100">
              {value !== undefined ? formatValue(value) : '—'}
            </div>
            {subtitle && (
              <div className="mt-1 text-sm leading-relaxed font-normal text-gray-500 dark:text-gray-400">
                {subtitle}
              </div>
            )}
          </div>
        </CardContent>

        <CardFooter className="mt-auto">
          {change && (
            <div
              className={twMerge(
                'inline-flex items-center rounded-full px-2 py-1 text-xs font-medium tracking-wide',
                isPositiveChange
                  ? 'bg-green-50 text-green-700 ring-1 ring-green-600/20 dark:bg-green-950 dark:text-green-300'
                  : 'bg-red-50 text-red-700 ring-1 ring-red-600/20 dark:bg-red-950 dark:text-red-300',
              )}
            >
              {change}
            </div>
          )}
          {renderChart()}
        </CardFooter>
      </div>
    </Card>
  );
};
